import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

const shuffle = (items) => {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const interpolate = (message, values) =>
  message.replace(/%\{(\w+)\}/g, (match, key) => values[key] ?? match);

// Fork of VisualWordQuiz's standalone BaseWord flow: optional images,
// base-to-target choices, local scoring, and an ask after the results.
const WordLadderQuiz = ({
  words,
  pair,
  targetLanguageCode,
  baseLanguageCode,
  baseLanguageName,
  targetLanguageName,
  messages,
  ctas,
}) => {
  const questions = useMemo(() => {
    const pool = words
      .filter((word) => word.translatedText?.trim() && word.baseText?.trim())
      .slice(0, 5);
    return pool.map((word) => ({
      ...word,
      options: shuffle([
        word,
        ...shuffle(pool.filter((other) => other.id !== word.id)).slice(0, 3),
      ]),
    }));
  }, [words]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [score, setScore] = useState(0);
  const [complete, setComplete] = useState(false);
  const answered = useRef(false);
  const tracked = useRef(false);
  const started = useRef(false);
  const questionRef = useRef(null);
  const resultRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    if (complete) resultRef.current?.focus();
    else if (selectedId !== null) nextRef.current?.focus();
    else if (currentIndex > 0) questionRef.current?.focus();
  }, [complete, currentIndex, selectedId]);

  if (questions.length < 5) return null;

  const currentWord = questions[currentIndex];
  const total = questions.length;
  const promptText = interpolate(messages.prompt, {
    base_language: baseLanguageName,
    target_language: targetLanguageName,
  });
  const number = (value) => messages.numbers[value];

  const selectAnswer = (id) => {
    if (answered.current) return;
    answered.current = true;
    if (!started.current) {
      started.current = true;
      window.footprint?.track("word_quiz_started", {
        pair,
        total,
        direction: "base_to_target",
      });
    }
    setSelectedId(id);
    if (id === currentWord.id) setScore((value) => value + 1);
  };

  const nextWord = () => {
    if (!answered.current || tracked.current) return;
    if (currentIndex === total - 1) {
      setComplete(true);
      // Track in the completion handler, once, even on repeated clicks.
      // Footprint is deliberately absent when analytics is disabled.
      tracked.current = true;
      window.footprint?.track("word_quiz_complete", {
        pair,
        score,
        total,
        direction: "base_to_target",
      });
    } else {
      answered.current = false;
      setSelectedId(null);
      setCurrentIndex((value) => value + 1);
    }
  };

  if (complete) {
    return (
      <div>
        <p
          ref={resultRef}
          tabIndex={-1}
          className="text-2xl font-black tracking-tight text-stone-900"
        >
          {interpolate(messages.score, {
            score: number(score),
            total: number(total),
          })}
        </p>
        <p className="mt-3 text-stone-600">
          {interpolate(messages.complete, {
            total: number(total),
            base_language: baseLanguageName,
            target_language: targetLanguageName,
          })}
        </p>
        <ul className="mt-4 space-y-2">
          {questions.map((word) => (
            <li key={word.id}>
              <strong lang={targetLanguageCode}>{word.translatedText}</strong>
              {" · "}
              <span lang={baseLanguageCode}>{word.baseText}</span>
            </li>
          ))}
        </ul>
        <div className="grid gap-3 mt-5">
          {ctas.map((cta, index) => (
            <a
              key={cta.href}
              href={`${cta.href}${cta.href.includes("?") ? "&" : "?"}words=${questions.map((word) => word.id).join(",")}`}
              onClick={() =>
                window.footprint?.track("keep_words_clicked", { pair, total })
              }
              lang={cta.lang}
              className={`flex items-center justify-center rounded-full px-4 py-3 text-center font-black transition hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-stone-900 ${
                index === 0
                  ? "bg-stone-900 text-white shadow-[3px_3px_0_#22c55e]"
                  : "border-2 border-stone-900 bg-white text-stone-900 hover:bg-stone-100"
              }`}
            >
              {messages.keep_words}
            </a>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-stone-500">
        {interpolate(messages.progress, {
          current: number(currentIndex + 1),
          total: number(total),
        })}
      </p>
      <p className="text-stone-600">{promptText}</p>
      <p
        ref={questionRef}
        tabIndex={-1}
        lang={baseLanguageCode}
        className="text-3xl font-black tracking-tight text-stone-900 break-words"
      >
        {currentWord.baseText}
      </p>
      {/* The picture used to sit here, above the options. On "choose the English
          meaning of mbwa" with a photo of a dog and four options, you can answer
          without knowing any Swahili at all — the image was the answer key. It
          only reads as a hint on the words that happen to have art, which is why
          it also felt inconsistent: 46 of 48 animals have images, the rest do
          not. It now appears after the answer, where a picture is a memory aid
          rather than a crutch. */}
      <div
        className="grid grid-cols-2 gap-3"
        role="group"
        aria-label={promptText}
      >
        {currentWord.options.map((option) => (
          <button
            key={option.id}
            type="button"
            lang={targetLanguageCode}
            disabled={selectedId !== null}
            aria-pressed={selectedId === option.id}
            onClick={() => selectAnswer(option.id)}
            className={`rounded-xl border-2 px-4 py-3 text-lg font-bold break-words focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-900 ${
              selectedId !== null && option.id === currentWord.id
                ? "border-green-700 bg-green-100 text-green-900"
                : selectedId === option.id
                  ? "border-red-700 bg-red-100 text-red-900"
                  : "border-stone-900 bg-white text-stone-900 enabled:hover:bg-stone-100"
            }`}
          >
            {option.translatedText}
          </button>
        ))}
      </div>
      <p role="status" className="text-stone-900">
        {selectedId !== null &&
          (selectedId === currentWord.id
            ? messages.correct
            : interpolate(messages.incorrect, {
                base_language: baseLanguageName,
                target_language: targetLanguageName,
                answer: currentWord.translatedText,
              }))}
      </p>
      {selectedId !== null && currentWord.imageUrl && (
        <img
          src={currentWord.imageUrl}
          alt=""
          width={96}
          height={96}
          loading="lazy"
          className="w-24 h-24 rounded-xl border-2 border-stone-900 object-cover"
        />
      )}
      {selectedId !== null && (
        <button
          ref={nextRef}
          type="button"
          onClick={nextWord}
          className="w-full rounded-full bg-stone-900 px-4 py-3 font-black text-white shadow-[3px_3px_0_#22c55e] transition hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-stone-900"
        >
          {currentIndex === total - 1 ? messages.finish : messages.next}
        </button>
      )}
    </div>
  );
};

WordLadderQuiz.propTypes = {
  words: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      englishText: PropTypes.string.isRequired,
      baseText: PropTypes.string.isRequired,
      translatedText: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
  ).isRequired,
  pair: PropTypes.string.isRequired,
  targetLanguageCode: PropTypes.string.isRequired,
  baseLanguageCode: PropTypes.string.isRequired,
  baseLanguageName: PropTypes.string.isRequired,
  targetLanguageName: PropTypes.string.isRequired,
  messages: PropTypes.shape({
    prompt: PropTypes.string.isRequired,
    progress: PropTypes.string.isRequired,
    correct: PropTypes.string.isRequired,
    incorrect: PropTypes.string.isRequired,
    next: PropTypes.string.isRequired,
    finish: PropTypes.string.isRequired,
    score: PropTypes.string.isRequired,
    complete: PropTypes.string.isRequired,
    keep_words: PropTypes.string.isRequired,
    numbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  ctas: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      lang: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default WordLadderQuiz;
